import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useOrganizationConfig } from "./organizationConfig";
import { db } from "@/mixins/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Unsubscribe,
  where,
} from "firebase/firestore";
import { OrgAndCustShared, organizationsAndCustomersConverter } from "@/mixins/converters";
import type { DocumentReference, FirestoreError } from "firebase/firestore";

const GET_DOC_ATTEMPTS = 10;
const GET_DOC_RETRY_MS = 500;

/**
 * getDoc that retries while Firestore reports the client as offline. Right after a sign-in
 * Firestore restarts its streams for the new credentials, and a getDoc for an uncached doc in that
 * window fails at once with `unavailable` instead of waiting for the connection.
 */
async function getDocWhenOnline<T>(docRef: DocumentReference<T>) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await getDoc(docRef);
    } catch (error) {
      const offline = (error as FirestoreError | undefined)?.code === "unavailable";
      if (!offline || attempt >= GET_DOC_ATTEMPTS) throw error;
      await new Promise((resolve) => setTimeout(resolve, GET_DOC_RETRY_MS));
    }
  }
}

export const useEntityStore = defineStore("entity", () => {
  const entity = ref<OrgAndCustShared | null>(null);
  const hasChildren = ref(false);

  const unsubscribeFn = ref<Unsubscribe | null>(null);
  const config = computed(() => {
    return useOrganizationConfig();
  });

  async function checkChildren(id: string) {
    const collectionRef = collection(db, "organizations_and_customers");
    const queryRef = query(collectionRef, where("ancestors", "array-contains", id));
    const res = await getDocs(queryRef);
    hasChildren.value = !res.empty;
  }

  async function loadEntity(id: string, configId?: string | null) {
    const docRef = doc(db, "organizations_and_customers", id).withConverter(
      organizationsAndCustomersConverter,
    );

    const snap = await getDocWhenOnline(docRef);
    if (snap.exists()) {
      entity.value = snap.data();
      config.value.setEntity(entity.value);
      // not awaited: hasChildren is only used reactively in templates
      checkChildren(id).catch(console.error);
      unsubscribeFn.value?.();
      unsubscribeFn.value = onSnapshot(docRef, (snap) => {
        if (snap.exists()) {
          entity.value = snap.data();
          config.value.setEntity(entity.value);
          checkChildren(id);
        }
      });

      await config.value.load(configId);
    } else {
      unsubscribeFn.value?.();
      entity.value = null;
      config.value.setEntity(null);
      config.value.reset();
    }
  }

  function $reset() {
    entity.value = null;
    unsubscribeFn.value?.();
    config.value.setEntity(null);
    config.value.reset();
  }

  return {
    fullObject: entity,
    hasChildren,
    loadEntity,
    $reset,
    config,
  };
});
